import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { BreadcrumbJsonLd } from '../../components/StructuredData';

const features = [
  {
    group: 'Evidence Collection',
    items: [
      {
        title: 'Audit Timeline',
        href: '/features/audit-timeline',
        description: 'Chronological record of all software changes — PR approvals, CI checks, merges, and deployments — generated from GitHub metadata.',
        badges: ['SOC 2 CC8.1', 'ISO 27001 A.8.32'],
      },
      {
        title: 'Manual Evidence Upload',
        href: appLinks.register,
        description: 'Upload files, screenshots, and documents as evidence items. Each file is checksum-verified (SHA-256), linked to a specific control and framework, and goes through a submit → review → approve workflow with full audit trail.',
        badges: ['SOC 2', 'ISO 27001', 'Tamper-evident'],
      },
      {
        title: 'PR Approvals Evidence',
        href: '/tools/pr-approvals-report',
        description: 'Exports reviewer identities, approval timestamps, and required check results per merged pull request. Available in guest mode — no account needed.',
        badges: ['Free tool', 'Guest mode'],
      },
      {
        title: 'CI Evidence',
        href: '/ci-evidence',
        description: 'Captures CI pipeline results, required status check outcomes, and build metadata linked to each change event.',
        badges: ['SOC 2 CC8.1'],
      },
      {
        title: 'Work Item Linking',
        href: '/work-item-linking',
        description: 'Links pull requests to external issue trackers so auditors can trace changes back to business requirements.',
        badges: ['Change traceability'],
      },
    ],
  },
  {
    group: 'Control Monitoring',
    items: [
      {
        title: 'Drift Detection',
        href: '/features/drift-detection',
        description: 'Monitors branch protection rules continuously. Captures before/after snapshots when settings change and classifies severity.',
        badges: ['ISO 27001 A.8.9', 'SOC 2 CC6.1'],
      },
      {
        title: 'Branch Protection Snapshot',
        href: '/tools/branch-protection-snapshot',
        description: 'Point-in-time export of branch protection configuration for any repository. Tamper-evident pack ready for auditors.',
        badges: ['Free tool', 'Guest mode'],
      },
      {
        title: 'Controls',
        href: appLinks.register,
        description: 'Define and manage compliance rules that are evaluated continuously against your GitHub activity. Auto-import from SDLC standards or create custom controls.',
        badges: ['SOC 2', 'ISO 27001'],
      },
      {
        title: 'Coverage Dashboard',
        href: appLinks.register,
        description: 'See which controls are passing, failing, or have gaps across all repositories. Identify exactly where evidence is missing before auditors do.',
        badges: ['Gap analysis'],
      },
    ],
  },
  {
    group: 'Exception & Risk Management',
    items: [
      {
        title: 'Exception Tracking',
        href: '/features/exception-tracking',
        description: 'Documents changes that bypass normal controls — missing approvals, failed checks, direct pushes. Exceptions can be acknowledged with context and rationale.',
        badges: ['Audit trail', 'SOC 2 CC7.2'],
      },
      {
        title: 'AI Risk Classifier',
        href: '/tools/ai-risk-classifier',
        description: 'Classifies your AI tools against EU AI Act risk tiers. Identifies which systems require human oversight documentation.',
        badges: ['EU AI Act', 'Free tool'],
      },
      {
        title: 'Reviews',
        href: appLinks.register,
        description: 'Collaborative review dashboard where auditors or internal reviewers can inspect evidence packs, add notes, and sign off.',
        badges: ['Auditor access'],
      },
    ],
  },
  {
    group: 'Sharing & Integration',
    items: [
      {
        title: 'Secure Evidence Sharing',
        href: '/secure-evidence-sharing',
        description: 'Generate time-limited, read-only share links for evidence packs. Auditors get direct access without needing an account.',
        badges: ['No auditor login required'],
      },
      {
        title: 'Read-Only GitHub Connection',
        href: '/read-only-github-audit',
        description: 'Connects via a read-only GitHub App. No write access. No code stored. Only structured event metadata is captured.',
        badges: ['Zero write access'],
      },
    ],
  },
];

export default function FeaturesPage() {
  return (
    <MarketingLayout
      title="Features | EvidentTrail"
      description="All EvidentTrail features: audit timeline, drift detection, exception tracking, PR approvals evidence, branch protection snapshots, coverage dashboard, and more."
      h1="Features"
      heroText="Everything you need to capture, monitor, and share GitHub evidence for SOC 2, ISO 27001, and EU AI Act audits — continuously, automatically, and without spreadsheets."
      type="website"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Features', url: 'https://evidenttrail.com/features' },
        ]}
      />

      <section className="py-16 md:py-20">
        <div className="max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {features.map((group) => (
            <div key={group.group}>
              <h2 className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-6">
                {group.group}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
                {group.items.map((item) => (
                  <Link
                    key={item.title}
                    to={item.href}
                    className="group block bg-slate-800/60 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 hover:bg-slate-800 transition-all"
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-base font-semibold text-slate-100 group-hover:text-blue-400 transition-colors">
                        {item.title}
                      </h3>
                      <svg className="w-4 h-4 text-slate-500 group-hover:text-blue-400 flex-shrink-0 mt-0.5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{item.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {item.badges.map((badge) => (
                        <span key={badge} className="inline-block text-xs bg-slate-700/60 text-slate-300 px-2 py-0.5 rounded-full">
                          {badge}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-slate-800">
        <div className="max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Start with a free tool — no account needed
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Guest mode gives you access to three free evidence tools instantly, with 24-hour retention and no GitHub write access required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/tools" className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              Explore free tools
            </Link>
            <a href={appLinks.register} className="inline-flex items-center justify-center gap-2 border border-slate-600 text-slate-300 hover:border-blue-500 hover:text-blue-400 font-semibold px-6 py-3 rounded-lg transition-colors">
              Get full access
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
